import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../utils/api';
import { FiArrowLeft, FiUpload } from 'react-icons/fi';

const AddProduct = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Clothes',
    brand: '',
    size: '',
    color: '',
    condition: 'Good',
    pricePerDay: '',
    pricePerHour: '',
    securityCharge: '',
    images: []
  });

  const categories = ['Clothes', 'Shoes', 'Boots', 'Accessories'];
  const conditions = ['New', 'Like New', 'Good', 'Fair'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const files = e.target.files;
    if (files.length === 0) return;

    // Limit to 5 images
    if (imagePreview.length + files.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    const previews = [];
    const imageArray = [];
    let loadedCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file size (max 10MB per file)
      if (file.size > 10 * 1024 * 1024) {
        setError(`Image ${i + 1} is too large. Max size is 10MB.`);
        return;
      }

      const reader = new FileReader();

      reader.onloadend = () => {
        previews.push(reader.result);
        imageArray.push(reader.result);
        loadedCount++;
        
        if (loadedCount === files.length) {
          setImagePreview([...imagePreview, ...previews]);
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, ...imageArray]
          }));
          setError(''); // Clear any previous errors
        }
      };

      reader.onerror = () => {
        setError(`Error reading file ${i + 1}`);
      };

      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    const newPreviews = imagePreview.filter((_, i) => i !== index);
    const newImages = formData.images.filter((_, i) => i !== index);
    setImagePreview(newPreviews);
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title || !formData.description || !formData.pricePerDay || !formData.securityCharge) {
      setError('Please fill in all required fields');
      return;
    }

    if (!formData.category) {
      setError('Please select a category');
      return;
    }

    if (formData.images.length === 0) {
      setError('Please upload at least one image');
      return;
    }

    if (formData.images.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    try {
      setLoading(true);
      
      // Check if images are too large
      const totalImageSize = formData.images.reduce((acc, img) => acc + img.length, 0);
      if (totalImageSize > 5 * 1024 * 1024) {
        setError('Total image size exceeds 5MB limit');
        setLoading(false);
        return;
      }

      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        brand: formData.brand.trim(),
        size: formData.size.trim(),
        color: formData.color.trim(),
        condition: formData.condition,
        pricePerDay: parseFloat(formData.pricePerDay),
        pricePerHour: formData.pricePerHour ? parseFloat(formData.pricePerHour) : 0,
        securityCharge: parseFloat(formData.securityCharge),
        images: formData.images
      };

      console.log('Submitting product with', payload.images.length, 'images');
      
      const response = await API.post('/products', payload);
      
      if (response.data.success) {
        navigate('/seller/dashboard');
      } else {
        setError(response.data.message || 'Error adding product');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Error adding product';
      setError(errorMessage);
      console.error('Full error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9D4] py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/seller/dashboard')}
            className="flex items-center space-x-2 text-[#2D2D2D] hover:text-[#F27D16] transition"
          >
            <FiArrowLeft className="text-xl" />
            <span>Back to Dashboard</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-[#386646] mb-2">Add New Product</h1>
          <p className="text-gray-600 mb-8">Fill in the details below to list your product</p>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-[#222222] mb-2">
                Product Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter product title"
                maxLength="100"
                className="input-field w-full"
                required
              />
              <p className="text-xs text-gray-500 mt-1">{formData.title.length}/100</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-[#222222] mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your product in detail"
                maxLength="2000"
                rows="5"
                className="textarea-field w-full"
                required
              />
              <p className="text-xs text-gray-500 mt-1">{formData.description.length}/2000</p>
            </div>

            {/* Category & Condition */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="input-field w-full"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">
                  Condition <span className="text-red-500">*</span>
                </label>
                <select
                  name="condition"
                  value={formData.condition}
                  onChange={handleChange}
                  className="input-field w-full"
                >
                  {conditions.map(cond => (
                    <option key={cond} value={cond}>{cond}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Brand, Size & Color */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">
                  Brand
                </label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  placeholder="e.g., Nike, Adidas"
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">
                  Size
                </label>
                <input
                  type="text"
                  name="size"
                  value={formData.size}
                  onChange={handleChange}
                  placeholder="e.g., M, L, 42"
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">
                  Color
                </label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  placeholder="e.g., Black, Blue"
                  className="input-field w-full"
                />
              </div>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">
                  Price Per Day (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="pricePerDay"
                  value={formData.pricePerDay}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="input-field w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">
                  Price Per Hour (₹)
                </label>
                <input
                  type="number"
                  name="pricePerHour"
                  value={formData.pricePerHour}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="input-field w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">
                  Security Charge (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="securityCharge"
                  value={formData.securityCharge}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="input-field w-full"
                  required
                />
              </div>
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-[#222222] mb-2">
                Product Images <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-[#F27D16] rounded-lg p-6 text-center cursor-pointer hover:bg-orange-50 transition">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  id="image-input"
                />
                <label htmlFor="image-input" className="cursor-pointer">
                  <FiUpload className="text-3xl text-[#F27D16] mx-auto mb-2" />
                  <p className="text-[#222222] font-medium">Click to upload images</p>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                </label>
              </div>

              {/* Image Previews */}
              {imagePreview.length > 0 && (
                <div className="mt-6">
                  <p className="text-sm font-medium text-[#222222] mb-4">Preview ({imagePreview.length} images)</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {imagePreview.map((preview, index) => (
                      <div key={index} className="relative">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/seller/dashboard')}
                className="flex-1 px-6 py-2 border-2 border-[#222222] text-[#222222] font-medium rounded-lg hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 btn-primary font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding Product...' : 'Add Product'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;
